import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  phone: "",
  email: "",
  vehicle_type: "Auto Urbana",
  pickup_location: "",
  pickup_date: "",
  return_date: "",
  notes: "",
};

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 transition-shadow duration-300";

export const QuoteForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontattiamo al più presto.");
    } catch (err) {
      toast.error("Qualcosa è andato storto. Riprova o chiamaci al +39 377 389 9146.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="preventivo" className="py-24 sm:py-32 bg-slate-900 grain relative" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-mono font-semibold uppercase tracking-[0.25em] text-emerald-400 mb-4">Preventivo express</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Raccontaci cosa ti serve. Al resto pensiamo noi.
          </h2>
          <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-md">
            Compila il modulo in meno di un minuto: un operatore ti ricontatterà con la proposta migliore per date, veicolo e budget.
          </p>
          <div className="mt-10 rounded-2xl border border-slate-700 bg-slate-800/60 p-6">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-400">Preferisci parlare con noi?</p>
            <a
              href="tel:+393773899146"
              data-testid="quote-phone-link"
              className="mt-2 inline-block font-display text-2xl font-extrabold text-emerald-400 hover:text-emerald-300 transition-colors duration-300"
            >
              +39 377 389 9146
            </a>
          </div>
          <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-6" data-testid="deposit-notice">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400">Cauzione trasparente</p>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed">
              La cauzione va pagata al momento della conferma della prenotazione, insieme alla somma del noleggio,
              e verrà restituita integralmente alla riconsegna della vettura.
            </p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          {done ? (
            <div className="rounded-3xl border border-emerald-500/40 bg-emerald-500/10 p-10 sm:p-14 text-center" data-testid="quote-success-message">
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
              <h3 className="mt-6 font-display text-2xl font-bold text-white">Richiesta ricevuta!</h3>
              <p className="mt-3 text-sm text-slate-300 max-w-sm mx-auto leading-relaxed">
                Grazie {form.name.split(" ")[0]}. Un operatore ti ricontatterà al numero {form.phone} il prima possibile.
              </p>
              <button
                onClick={() => { setDone(false); setForm(INITIAL); }}
                data-testid="quote-new-request-button"
                className="mt-8 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-200 hover:bg-slate-800 hover:text-white transition-colors duration-300"
              >
                Invia un'altra richiesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-slate-700 bg-slate-800/40 backdrop-blur p-6 sm:p-10" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="q-name" className="block text-xs font-semibold text-slate-300 mb-2">Nome e cognome *</label>
                  <input id="q-name" required value={form.name} onChange={set("name")} placeholder="Mario Rossi" className={inputCls} data-testid="quote-input-name" />
                </div>
                <div>
                  <label htmlFor="q-phone" className="block text-xs font-semibold text-slate-300 mb-2">Telefono *</label>
                  <input id="q-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+39 333 000 0000" className={inputCls} data-testid="quote-input-phone" />
                </div>
                <div>
                  <label htmlFor="q-email" className="block text-xs font-semibold text-slate-300 mb-2">Email *</label>
                  <input id="q-email" required type="email" value={form.email} onChange={set("email")} placeholder="mario@esempio.it" className={inputCls} data-testid="quote-input-email" />
                </div>
                <div>
                  <label htmlFor="q-vehicle" className="block text-xs font-semibold text-slate-300 mb-2">Tipo di veicolo *</label>
                  <select id="q-vehicle" value={form.vehicle_type} onChange={set("vehicle_type")} className={inputCls} data-testid="quote-select-vehicle-type">
                    <option>Auto Urbana</option>
                    <option>Berlina / SUV</option>
                    <option>Furgone / Veicolo Commerciale</option>
                    <option>Non so — consigliami tu</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="q-location" className="block text-xs font-semibold text-slate-300 mb-2">Luogo di ritiro</label>
                  <input id="q-location" value={form.pickup_location} onChange={set("pickup_location")} placeholder="Città, aeroporto, stazione…" className={inputCls} data-testid="quote-input-pickup-location" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="q-pickup" className="block text-xs font-semibold text-slate-300 mb-2">Dal</label>
                    <input id="q-pickup" type="date" value={form.pickup_date} onChange={set("pickup_date")} className={inputCls} data-testid="quote-input-pickup-date" />
                  </div>
                  <div>
                    <label htmlFor="q-return" className="block text-xs font-semibold text-slate-300 mb-2">Al</label>
                    <input id="q-return" type="date" value={form.return_date} onChange={set("return_date")} className={inputCls} data-testid="quote-input-return-date" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="q-notes" className="block text-xs font-semibold text-slate-300 mb-2">Note</label>
                  <textarea id="q-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="Es. seggiolino bimbo, secondo conducente, consegna in hotel…" className={inputCls} data-testid="quote-input-notes" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-button"
                className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-8 py-4 text-sm font-bold text-white hover:bg-emerald-500 hover:text-white disabled:opacity-60 transition-colors duration-300"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Invio in corso…" : "Invia richiesta di preventivo"}
              </button>
              <p className="mt-4 text-center text-xs text-slate-500">
                Inviando accetti di essere ricontattato dal nostro operatore. Nessuno spam, promesso.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
